import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft,
    CreditCard,
    Building2,
    AlertCircle,
    History,
    Check,
    CheckCircle2,
    ArrowRight,
    Layers
} from 'lucide-react';
import './CompanyManagement.css';
import './CompanySubscriptionDetails.css';

const getPlanItems = (plan) => {
    if (!plan) return [];
    if (Array.isArray(plan.item_ids) && plan.item_ids.length > 0) {
        return plan.item_ids.map(item => (typeof item === 'object' && item !== null) ? item : { _id: item, item_code: 'ITEM', item_name: 'Item' });
    }
    if (plan.item_id) {
        const item = plan.item_id;
        return [(typeof item === 'object' && item !== null) ? item : { _id: item, item_code: 'ITEM', item_name: 'Item' }];
    }
    return [];
};

const CompanySubscriptionDetails = () => {
    const { companyId } = useParams();
    const navigate = useNavigate();

    const [company, setCompany] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [history, setHistory] = useState([]);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [submittingPlanId, setSubmittingPlanId] = useState(null);
    const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'available'

    const serverUrl = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:8080';

    useEffect(() => {
        const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
        if (userRole !== 'superadmin' && userRole !== 'admin') {
            navigate('/dashboard', { replace: true });
            return;
        }
        fetchSubscriptionData();
    }, [companyId]);

    const fetchSubscriptionData = async () => {
        setIsLoading(true);
        try {
            const [compRes, subRes, plansRes] = await Promise.all([
                fetch(`${serverUrl}/api/companies`),
                fetch(`${serverUrl}/api/subscriptions/company/${companyId}`),
                fetch(`${serverUrl}/api/subscription-plans`)
            ]);

            if (compRes.ok) {
                const comps = await compRes.json();
                const currentComp = comps.find(c => c._id === companyId);
                setCompany(currentComp || { _id: companyId, name: 'Organization', code: 'ORG' });
            }

            if (subRes.ok) {
                const subData = await subRes.json();
                setSubscription(subData.subscription);
                setHistory(subData.history || []);
            }

            if (plansRes.ok) {
                const plansData = await plansRes.json();
                setAvailablePlans(plansData);
            }
        } catch (err) {
            console.log('Server unreachable, using fallback local subscription data');
            const mockComp = {
                _id: companyId || 'comp_1',
                name: 'Federal Steel Industries',
                code: 'FSI-001',
                contactEmail: 'contact@federalsteel.com'
            };
            setCompany(mockComp);

            const mockPlan = {
                _id: 'plan_cu_1',
                plan_name: 'Copper Standard Monthly',
                rate: 5000,
                currency: 'USD',
                billing_interval: 'MONTH',
                item_id: { item_code: 'COPPER', item_name: 'Copper Metallurgy' }
            };

            setSubscription({
                _id: 'sub_demo_1',
                party_type: 'CUSTOMER',
                customer_id: companyId,
                status: 'ACTIVE',
                plans: [{ plan_id: mockPlan, quantity: 1 }],
                subscription_start_date: new Date(Date.now() - 30 * 86400000).toISOString()
            });

            setAvailablePlans([
                mockPlan,
                {
                    _id: 'plan_fe_1',
                    plan_name: 'Iron & Steel Enterprise',
                    rate: 12000,
                    currency: 'USD',
                    billing_interval: 'YEAR',
                    item_id: { item_code: 'IRON', item_name: 'Iron & Steel Metallurgy' }
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubscribe = async (planId) => {
        setSubmittingPlanId(planId);
        setActionMessage({ type: '', text: '' });
        try {
            const response = await fetch(`${serverUrl}/api/subscriptions/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ companyId, planId })
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to subscribe to plan.');
            }

            setSubscription(data.subscription);
            if (data.history) setHistory(data.history);
            setActionMessage({ type: 'success', text: data.message || 'Subscription updated successfully!' });
            setActiveTab('overview');
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        } finally {
            setSubmittingPlanId(null);
        }
    };

    const isSuperAdmin = !window.location.pathname.includes('/admin/subscription');
    const currentPlan = subscription?.plans?.[0]?.plan_id;

    if (isLoading) {
        return (
            <div className="admin-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <div className="glass-panel" style={{ padding: '30px 50px', textAlign: 'center' }}>
                    <div className="stat-icon" style={{ margin: '0 auto 16px', background: 'rgba(14, 165, 233, 0.12)', color: 'var(--primary-color)' }}>
                        <CreditCard size={32} />
                    </div>
                    <h3 style={{ margin: 0 }}>Loading Subscription Details…</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            {/* Header Section */}
            <div className="admin-header-row" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={() => navigate(-1)} className="btn-secondary" style={{ padding: '10px 14px' }}>
                        <ArrowLeft size={18} /> Back
                    </button>
                    <div>
                        <h2 className="text-gradient" style={{ margin: 0, fontSize: '26px' }}>Subscription & Billing Details</h2>
                        {company && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                <Building2 size={16} color="var(--primary-color)" />
                                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{company.name}</span>
                                {company.code && <span className="company-code-badge">{company.code}</span>}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Notification Banner */}
            {actionMessage.text && (
                <div
                    className="glass-panel"
                    style={{
                        padding: '12px 20px',
                        marginBottom: '20px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        background: actionMessage.type === 'error' ? '#fee2e2' : '#dcfce7',
                        color: actionMessage.type === 'error' ? '#991b1b' : '#166534',
                        borderColor: actionMessage.type === 'error' ? '#fca5a5' : '#86efac'
                    }}
                >
                    {actionMessage.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>{actionMessage.text}</span>
                </div>
            )}

            {/* Tab Navigation Bar */}
            {!isSuperAdmin && (
                <div className="sub-tabs-container">
                    <button
                        type="button"
                        className={`sub-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <CreditCard size={16} /> My Subscription
                    </button>
                    <button
                        type="button"
                        className={`sub-tab-btn ${activeTab === 'available' ? 'active' : ''}`}
                        onClick={() => setActiveTab('available')}
                    >
                        <Layers size={16} /> Explore Plans
                        {availablePlans.length > 0 && <span className="sub-tab-badge">{availablePlans.length}</span>}
                    </button>
                </div>
            )}

            {/* Tab Content 1: My Subscription Overview & Billing History */}
            {(activeTab === 'overview' || isSuperAdmin) && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                >
                    {/* Active Subscription Card */}
                    <div className="subscription-overview-grid">
                        <div className="glass-panel sub-card">
                            <div className="sub-card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <CreditCard size={22} color="var(--primary-color)" />
                                    <h3 style={{ margin: 0, fontSize: '18px' }}>Active Subscription</h3>
                                </div>
                                <span className={`status-tag ${subscription?.status?.toLowerCase() || 'inactive'}`}>
                                    {subscription?.status || 'NO ACTIVE SUBSCRIPTION'}
                                </span>
                            </div>

                                    {subscription ? (
                                <div className="sub-card-content">
                                    <div className="sub-detail-row">
                                        <span className="sub-label">Current Plan:</span>
                                        <span className="sub-value bold">
                                            {currentPlan?.plan_name || currentPlan?.name || 'Standard Plan'}
                                        </span>
                                    </div>
                                    <div className="sub-detail-row">
                                        <span className="sub-label">Billing Interval:</span>
                                        <span className="sub-value">
                                            {currentPlan?.billing_interval || 'MONTH'}LY
                                        </span>
                                    </div>
                                    <div className="sub-detail-row">
                                        <span className="sub-label">Rate / Pricing:</span>
                                        <span className="sub-value">
                                            {subscription.plans?.[0]?.custom_rate !== undefined && subscription.plans?.[0]?.custom_rate !== null ? (
                                                <>
                                                    <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: '6px' }}>
                                                        {currentPlan?.currency || 'INR'} {currentPlan?.rate ? Number(currentPlan.rate).toLocaleString() : '—'}
                                                    </span>
                                                    <strong style={{ color: '#10b981' }}>
                                                        {currentPlan?.currency || 'INR'} {Number(subscription.plans[0].custom_rate).toLocaleString()}
                                                    </strong>
                                                    {/* <span style={{ fontSize: '11px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '2px 6px', borderRadius: '4px', marginLeft: '6px', fontWeight: 600 }}>
                                                        Negotiated Rate
                                                    </span> */}
                                                </>
                                            ) : (
                                                <>{currentPlan?.currency || 'INR'} {currentPlan?.rate ? Number(currentPlan.rate).toLocaleString() : '—'}</>
                                            )}
                                        </span>
                                    </div>
                                    <div className="sub-detail-row">
                                        <span className="sub-label">Start Date:</span>
                                        <span className="sub-value">
                                            {subscription.subscription_start_date ? new Date(subscription.subscription_start_date).toLocaleDateString() : '—'}
                                        </span>
                                    </div>
                                    <div className="sub-detail-row">
                                        <span className="sub-label">End Date:</span>
                                        <span className="sub-value">
                                            {history?.[0]?.period_end_date ? new Date(history[0].period_end_date).toLocaleDateString() : (subscription?.subscription_end_date ? new Date(subscription.subscription_end_date).toLocaleDateString() : '—')}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="sub-empty-state" style={{ padding: '30px', textAlign: 'center' }}>
                                    <AlertCircle size={32} color="var(--text-secondary)" style={{ marginBottom: '8px' }} />
                                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No active subscription plan found for this company.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Subscription & Billing History Table */}
                    <div className="table-panel glass-panel" style={{ marginTop: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <History size={20} color="var(--primary-color)" />
                            <h3 style={{ margin: 0, fontSize: '18px' }}>Subscription History</h3>
                        </div>
                        <table className="custom-table">
                            <thead>
                                <tr>
                                    <th>Plan Name</th>
                                    <th>Billed Rate</th>
                                    <th>Period Start</th>
                                    <th>Period End</th>
                                    <th>Billing Date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
                                            No historical subscription or billing period records found.
                                        </td>
                                    </tr>
                                ) : (
                                    history.map(item => {
                                        const planObj = item.plan_id || item.plan || currentPlan;
                                        const billedAmount = item.amount !== undefined && item.amount !== null && item.amount > 0 ? item.amount : (subscription?.plans?.[0]?.custom_rate || planObj?.rate);
                                        return (
                                            <tr key={item._id}>
                                                <td>
                                                    <span style={{ fontWeight: 600 }}>
                                                        {item.plan_name || item.plan?.plan_name || planObj?.plan_name || 'Standard Plan'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                                        {planObj?.currency || 'INR'} {billedAmount ? Number(billedAmount).toLocaleString() : '—'}
                                                    </span>
                                                </td>
                                                <td>{item.period_start_date ? new Date(item.period_start_date).toLocaleDateString() : '—'}</td>
                                                <td>{item.period_end_date ? new Date(item.period_end_date).toLocaleDateString() : '—'}</td>
                                                <td>{item.billing_date ? new Date(item.billing_date).toLocaleDateString() : '—'}</td>
                                                <td>
                                                    <span className={`status-tag ${item.status?.toLowerCase() || 'active'}`}>
                                                        {item.status || 'PAID'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            )}

            {/* Tab Content 2: Available Subscription Plans (Company Admin View) */}
            {activeTab === 'available' && !isSuperAdmin && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="available-plans-section"
                >
                    <div className="plans-grid-row">
                        {availablePlans.length === 0 ? (
                            <div className="glass-panel" style={{ padding: '30px', textAlign: 'center', gridColumn: '1 / -1', color: 'var(--text-secondary)' }}>
                                No subscription plans available.
                            </div>
                        ) : (
                            availablePlans.map(plan => {
                                const isCurrent = (currentPlan?._id || currentPlan) === plan._id || currentPlan?.plan_name === plan.plan_name;
                                const hasActivePlan = subscription && subscription.status === 'ACTIVE';
                                const planItems = getPlanItems(plan);

                                // Check if this company has a negotiated price override on this plan
                                const overrideEntry = Array.isArray(plan.company_overrides) ? plan.company_overrides.find(o => {
                                    const compIdStr = o.company_id?._id ? o.company_id._id.toString() : (o.company_id ? o.company_id.toString() : '');
                                    return compIdStr === (companyId || '').toString();
                                }) : null;

                                return (
                                    <motion.div
                                        key={plan._id}
                                        whileHover={{ scale: 1.02 }}
                                        className={`plan-card-item glass-panel ${isCurrent ? 'active-plan' : ''}`}
                                        style={{
                                            padding: '20px',
                                            borderRadius: '14px',
                                            border: isCurrent ? '2px solid var(--primary-color)' : '1px solid var(--panel-border)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            gap: '16px'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                            <div className="stat-icon" style={{ background: 'rgba(14, 165, 233, 0.1)', color: 'var(--primary-color)', padding: '8px', borderRadius: '10px' }}>
                                                <CreditCard size={20} />
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                                                {planItems.map(item => (
                                                    <span key={item._id || item.item_code || Math.random()} className="domain-tag" style={{ background: 'rgba(99, 102, 241, 0.12)', color: 'var(--primary-color)', fontSize: '11px', padding: '3px 7px', borderRadius: '6px' }}>
                                                        {item.item_name || item.item_code || 'FEATURE'}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 700 }}>{plan.plan_name}</h4>
                                            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--primary-color)', fontFamily: 'monospace' }}>
                                                {overrideEntry ? (
                                                    <div>
                                                        <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '16px', marginRight: '8px', color: 'var(--text-secondary)' }}>
                                                            {plan.currency || 'INR'} {Number(plan.rate).toLocaleString()}
                                                        </span>
                                                        <span style={{ color: '#10b981' }}>
                                                            {plan.currency || 'INR'} {Number(overrideEntry.negotiated_rate).toLocaleString()}
                                                        </span>
                                                        {/* <span style={{ fontSize: '11px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '2px 6px', borderRadius: '4px', marginLeft: '6px', fontWeight: 600 }}>
                                                            Negotiated Rate
                                                        </span> */}
                                                    </div>
                                                ) : (
                                                    <span>
                                                        {plan.currency || 'INR'} {Number(plan.rate).toLocaleString()}
                                                    </span>
                                                )}
                                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}> / {plan.billing_interval?.toLowerCase()}</span>
                                            </div>
                                        </div>

                                        <div>
                                            {isCurrent ? (
                                                <button className="btn-secondary" disabled style={{ width: '100%', justifyContent: 'center', opacity: 0.8, background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
                                                    <Check size={16} /> Current Active Plan
                                                </button>
                                            ) : hasActivePlan ? (
                                                <button
                                                    disabled
                                                    className="btn-secondary"
                                                    style={{ width: '100%', justifyContent: 'center', opacity: 0.5, cursor: 'not-allowed' }}
                                                    title="Cannot subscribe to another plan while an active plan exists."
                                                >
                                                    Subscribe Plan
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleSubscribe(plan._id)}
                                                    disabled={submittingPlanId === plan._id}
                                                    className="btn-primary"
                                                    style={{ width: '100%', justifyContent: 'center' }}
                                                >
                                                    {submittingPlanId === plan._id ? 'Activating…' : 'Subscribe Plan'} <ArrowRight size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })
                        )}
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default CompanySubscriptionDetails;
